// React and external dependencies
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

// Redux actions
import {
  updateContactIfNeeded as updateContactIfNeededAction,
  fetchContactMethods as fetchContactMethodsAction,
  clearContactMethodsError as clearContactMethodsErrorAction,
} from 'src/redux/contactMethods/actions';
import sendNotificationAction, { clearIncidentData as clearIncidentDataAction } from 'src/redux/incidents/actions';
import {
  deleteImmediateNotificationRules as deleteImmediateNotificationRulesAction,
  createNotificationRule as createNotificationRuleAction,
  fetchNotificationRules as fetchNotificationRulesAction,
} from 'src/redux/notificationRules/actions';
import {
  hasDownloadedMobileApp
} from 'src/redux/persona/selectors';
import {
  createService as createServiceAction,
  fetchServicesData as fetchServicesDataAction,
} from 'src/redux/services/actions';

// Redux selectors
import {
  getPhoneNumber,
  getEmailAddress,
  getCountryCode,
  getContactMethodErrors,
} from 'src/redux/contactMethods/selectors';
import {
  getFirstEscalationPolicyId,
  hasCreatedASchedule,
  getUser,
} from 'src/redux/fe-data/selectors';
import { getIncident, getIncidentError } from 'src/redux/incidents/selectors';
import { getServicesData, getFirstService, accountHasAnIntegration } from 'src/redux/services/selectors';

// Utilities
import { TRACKS_THIRD_ARM } from 'src/util/segment-storeless';

// Internal components and styling
import TrackedButton from 'src/components/TrackedButton';
import Modal from '../../Modal';
import ModalBody from '../../Modal/ModalBody';
import ModalFooter from '../../Modal/ModalFooter';
import ServiceStep from './ServiceStep';
import NotificationStep from './NotificationStep';
import MobileStep from './MobileStep';
import ScheduleStep from './ScheduleStep';
import IntegrationsStep from './IntegrationsStep';
import styles from './style.module.css';

function GoalsModal(props) {
  const {
    // Services step props
    firstEscalationPolicyId, createService, fetchServicesData,
    firstService, isServiceApiFetching, serviceApiError,
    // Notification step props
    fetchNotificationRules, fetchContactMethods, user, deleteImmediateNotificationRules,
    updateContactIfNeeded, createNotificationRule, sendNotification,
    incident, incidentError, userEmail, userPhoneNumber,
    userCountryCode, contactMethodErrors, clearContactMethodsError,
    clearIncidentData,
    // Step completion props
    isMobileStepComplete, isScheduleStepComplete, isIntegrationStepComplete,
    // Modal management props
    showModal, closeModal,
    // Segment data props
    getSegmentProps,
  } = props;

  useEffect(() => {
    (async function loadServices() {
      await fetchServicesData();
      await fetchNotificationRules(user);
      await fetchContactMethods(user);
    }());
  }, []);

  return (
    <Modal
      namespace="onboardingExperiment"
      width="600px"
      showModal={showModal}
    >
      <ModalBody>
        <h5 className={styles.goalHeader}>
          PagerDuty is most powerful when you complete these steps to set your account up.
          Click on each step to complete it!
        </h5>
        <ServiceStep
          firstEscalationPolicyId={firstEscalationPolicyId}
          firstService={firstService}
          createService={createService}
          isServiceApiFetching={isServiceApiFetching}
          serviceApiError={serviceApiError}
          getSegmentProps={getSegmentProps}
        />
        <NotificationStep
          deleteImmediateNotificationRules={deleteImmediateNotificationRules}
          updateContactIfNeeded={updateContactIfNeeded}
          createNotificationRule={createNotificationRule}
          sendNotification={sendNotification}
          service={firstService}
          user={user}
          incident={incident}
          incidentError={incidentError}
          prefillEmail={userEmail}
          prefillPhone={userPhoneNumber}
          prefillCountryCode={userCountryCode}
          contactMethodErrors={contactMethodErrors}
          clearContactMethodsError={clearContactMethodsError}
          clearIncidentData={clearIncidentData}
          getSegmentProps={getSegmentProps}
        />
        <MobileStep
          isComplete={isMobileStepComplete}
          getSegmentProps={getSegmentProps}
        />
        <ScheduleStep
          isComplete={isScheduleStepComplete}
          getSegmentProps={getSegmentProps}
        />
        <IntegrationsStep
          isComplete={isIntegrationStepComplete}
          firstService={firstService}
          getSegmentProps={getSegmentProps}
        />
      </ModalBody>
      <ModalFooter>
        <TrackedButton
          type="button"
          color="link"
          data-test="exit-onboarding"
          className={styles.exitOnboarding}
          onClick={closeModal}
          trackName={TRACKS_THIRD_ARM.quit_onboarding}
          trackProperties={getSegmentProps()}
        >
          I&apos;ll finish onboarding later
        </TrackedButton>
      </ModalFooter>
    </Modal>
  );
}

GoalsModal.defaultProps = {
  firstService: {
    id: '',
    name: '',
  },
  serviceApiError: null,
};

GoalsModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  firstEscalationPolicyId: PropTypes.string.isRequired,
  createService: PropTypes.func.isRequired,
  fetchServicesData: PropTypes.func.isRequired,
  firstService: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  isServiceApiFetching: PropTypes.bool.isRequired,
  serviceApiError: PropTypes.instanceOf(Error),
  deleteImmediateNotificationRules: PropTypes.func.isRequired,
  updateContactIfNeeded: PropTypes.func.isRequired,
  createNotificationRule: PropTypes.func.isRequired,
  sendNotification: PropTypes.func.isRequired,
  fetchNotificationRules: PropTypes.func.isRequired,
  fetchContactMethods: PropTypes.func.isRequired,
  user: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  userEmail: PropTypes.string,
  userPhoneNumber: PropTypes.string,
  userCountryCode: PropTypes.string,
  incident: PropTypes.shape({
    id: PropTypes.string,
  }),
  incidentError: PropTypes.shape({
    message: PropTypes.string
  }),
  contactMethodErrors: PropTypes.arrayOf(PropTypes.string).isRequired,
  clearContactMethodsError: PropTypes.func.isRequired,
  clearIncidentData: PropTypes.func.isRequired,
  isScheduleStepComplete: PropTypes.bool.isRequired,
  isMobileStepComplete: PropTypes.bool.isRequired,
  isIntegrationStepComplete: PropTypes.bool.isRequired,
  getSegmentProps: PropTypes.func.isRequired,
};

GoalsModal.defaultProps = {
  userEmail: '',
  userPhoneNumber: '',
  userCountryCode: null,
  incident: {
    id: '',
  },
  incidentError: {
    message: '',
  },
};

const mapStateToProps = state => ({
  firstEscalationPolicyId: getFirstEscalationPolicyId(state),
  firstService: getFirstService(state),
  isServiceApiFetching: getServicesData(state).isFetching,
  serviceApiError: getServicesData(state).error,
  user: getUser(state),
  userPhoneNumber: getPhoneNumber(state),
  userCountryCode: getCountryCode(state).toString(),
  userEmail: getEmailAddress(state),
  incident: getIncident(state),
  incidentError: getIncidentError(state),
  contactMethodErrors: getContactMethodErrors(state),
  isScheduleStepComplete: hasCreatedASchedule(state),
  isMobileStepComplete: hasDownloadedMobileApp(state),
  isIntegrationStepComplete: accountHasAnIntegration(state),
});

const mapActionsToProps = {
  createService: createServiceAction,
  fetchServicesData: fetchServicesDataAction,
  deleteImmediateNotificationRules: deleteImmediateNotificationRulesAction,
  updateContactIfNeeded: updateContactIfNeededAction,
  createNotificationRule: createNotificationRuleAction,
  sendNotification: sendNotificationAction,
  fetchNotificationRules: fetchNotificationRulesAction,
  fetchContactMethods: fetchContactMethodsAction,
  clearContactMethodsError: clearContactMethodsErrorAction,
  clearIncidentData: clearIncidentDataAction,
};

export default connect(mapStateToProps, mapActionsToProps)(GoalsModal);
